import { forwardRef, useEffect, useState } from "react";
import { playClip, useVoiceServiceAvailable, usePlayingClipId, useEvaVoice, getVoiceId } from "./EvaPlayButton";
import { hasPlayed, markPlayed } from "@/lib/evaClipMemory";

/**
 * EvaVoiceTap — visible inline "◉ hear eva" chip at the end of a sentence.
 * Idle: amber dot + lowercase label, low opacity (0.55).
 * Hover/focus: opacity 1, background lifts, dot brightens.
 * Active: 3-bar EQ + "playing…" label.
 * First chip in viewport per session does a single 2s pulse-and-fade nudge.
 * Hidden entirely when voice service is unavailable (silent failure for an easter egg).
 * Reuses the singleton playback engine from EvaPlayButton, so only one clip
 * plays at a time across the whole page.
 */
type Props = {
  clipId: string;
  label?: string;
  /**
   * Optional short FAQ-style hint shown next to the dot, e.g. "why 24 hours?".
   * MUST be ≤4 words; longer strings are auto-truncated to dot-only mode to
   * prevent the chip from breaking card layouts.
   */
  hint?: string;
};

// Module-level: the first EvaVoiceTap to scroll into view in a given session
// gets the one-time "nudge" animation. Tracked via sessionStorage so it
// survives soft refreshes within the same tab.
const NUDGE_KEY = "eva_voice_tap_nudge_shown_v1";
let nudgeClaimed = false;
function tryClaimNudge(): boolean {
  if (nudgeClaimed) return false;
  try {
    if (sessionStorage.getItem(NUDGE_KEY)) { nudgeClaimed = true; return false; }
    sessionStorage.setItem(NUDGE_KEY, "1");
  } catch {}
  nudgeClaimed = true;
  return true;
}

export const EvaVoiceTap = forwardRef<HTMLSpanElement, Props>(({ clipId, label = "hear eva", hint }, forwardedRef) => {
  // Hint mode: only render visible text if hint is provided and ≤4 words.
  // Anything else (or no hint) → silent dot mode.
  const wordCount = hint ? hint.trim().split(/\s+/).length : 0;
  const showHint = !!hint && wordCount > 0 && wordCount <= 4;
  const visibleText = showHint ? hint!.trim() : null;
  const ariaLabel = visibleText ?? label;
  const available = useVoiceServiceAvailable();
  const playingId = usePlayingClipId();
  // Subscribe to voice changes so the chip re-renders (and re-evaluates its
  // "already played" gate) whenever the user picks a different voice.
  const { voiceId } = useEvaVoice();
  const [hovered, setHovered] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errored, setErrored] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [nudge, setNudge] = useState(false);
  const [chipEl, setChipEl] = useState<HTMLSpanElement | null>(null);
  const setRefs = (el: HTMLSpanElement | null) => {
    setChipEl(el);
    if (typeof forwardedRef === "function") forwardedRef(el);
    else if (forwardedRef) (forwardedRef as React.MutableRefObject<HTMLSpanElement | null>).current = el;
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const onChange = () => setReduceMotion(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  // First chip in viewport per session: do a 2s pulse-and-fade nudge.
  useEffect(() => {
    if (!chipEl || available !== true || reduceMotion) return;
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          if (tryClaimNudge()) {
            setNudge(true);
            window.setTimeout(() => setNudge(false), 2200);
          }
          io.disconnect();
          break;
        }
      }
    }, { threshold: 0.6 });
    io.observe(chipEl);
    return () => io.disconnect();
  }, [chipEl, available, reduceMotion]);

  // Service down (or still probing → null) → render nothing.
  if (available !== true) return null;

  const isPlaying = playingId === clipId;
  const active = hovered || isPlaying || nudge;

  const handleClick = async (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (loading) return;
    // Gate: each clip plays at most once per session AFTER a successful play.
    // If a previous attempt errored we always allow a retry.
    if (!errored && hasPlayed(clipId, getVoiceId())) return;
    try {
      setLoading(true);
      setErrored(false);
      await playClip(clipId);
      // Only mark as played after audio actually started — otherwise a failed
      // attempt would silently lock the chip for the rest of the session.
      markPlayed(clipId, getVoiceId());
    } catch (err) {
      console.error("[EvaVoiceTap]", clipId, err);
      setErrored(true);
      // Auto-clear the error state so the chip looks idle again after a beat.
      window.setTimeout(() => setErrored(false), 4000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <span
      ref={setRefs}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      title={ariaLabel}
      onClick={handleClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleClick(e); }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      className={nudge && !reduceMotion ? "evt-chip evt-chip-nudge" : "evt-chip"}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: visibleText ? 6 : 0,
        marginLeft: 8,
        verticalAlign: "baseline",
        padding: visibleText ? "3px 10px 3px 8px" : "5px 7px",
        borderRadius: 999,
        border: "1px solid rgba(196,130,58,0.35)",
        background: errored
          ? "rgba(196,80,40,0.14)"
          : active ? "rgba(196,130,58,0.14)" : "rgba(196,130,58,0.08)",
        opacity: errored ? 1 : (active ? 1 : 0.65),
        transition: "opacity 200ms ease, background 200ms ease",
        fontSize: 11,
        fontFamily: "Barlow, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        letterSpacing: "0.04em",
        color: errored
          ? "rgba(170,60,30,1)"
          : active ? "rgba(140,80,20,1)" : "rgba(196,130,58,0.85)",
        lineHeight: 1,
        userSelect: "none",
        cursor: "pointer",
        outline: "none",
        WebkitTapHighlightColor: "transparent",
        minHeight: 22,
      }}
    >
      {isPlaying ? (
        <>
          <span
            aria-hidden
            style={{
              display: "inline-flex",
              alignItems: "flex-end",
              gap: 1.5,
              height: 9,
              width: 10,
            }}
          >
            <span className="evt-bar" style={{ width: 2, background: "rgba(140,80,20,0.95)", borderRadius: 1, height: "60%" }} />
            <span className="evt-bar evt-bar-2" style={{ width: 2, background: "rgba(140,80,20,0.95)", borderRadius: 1, height: "100%" }} />
            <span className="evt-bar evt-bar-3" style={{ width: 2, background: "rgba(140,80,20,0.95)", borderRadius: 1, height: "75%" }} />
          </span>
          {visibleText ? <span>playing…</span> : null}
        </>
      ) : (
        <>
          <span
            aria-hidden
            className={(active || nudge) && !reduceMotion ? "evt-dot evt-dot-pulse" : "evt-dot"}
            style={{
              display: "inline-block",
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: errored
                ? "rgba(170,60,30,1)"
                : active ? "rgba(196,130,58,1)" : "rgba(196,130,58,0.7)",
              transition: "background 180ms ease",
            }}
          />
          {visibleText
            ? <span>{loading ? "…" : errored ? "tap to retry" : visibleText}</span>
            : (loading ? <span>…</span> : errored ? <span>retry</span> : null)}
        </>
      )}
      <style>{`
        @keyframes evt-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(196,130,58,0.45); }
          70%  { box-shadow: 0 0 0 7px rgba(196,130,58,0); }
          100% { box-shadow: 0 0 0 0 rgba(196,130,58,0); }
        }
        .evt-dot-pulse { animation: evt-pulse 2.4s ease-out infinite; }
        @keyframes evt-chip-nudge {
          0%   { box-shadow: 0 0 0 0 rgba(196,130,58,0.35); transform: scale(1); }
          40%  { box-shadow: 0 0 0 8px rgba(196,130,58,0); transform: scale(1.04); }
          100% { box-shadow: 0 0 0 0 rgba(196,130,58,0); transform: scale(1); }
        }
        .evt-chip-nudge { animation: evt-chip-nudge 2s ease-out 1; }
        @keyframes evt-bars { 0%,100% { transform: scaleY(0.4); } 50% { transform: scaleY(1); } }
        .evt-bar { transform-origin: bottom; animation: evt-bars 0.9s ease-in-out infinite; }
        .evt-bar-2 { animation-delay: 0.15s; }
        .evt-bar-3 { animation-delay: 0.3s; }
        @media (prefers-reduced-motion: reduce) {
          .evt-bar { animation: none !important; transform: scaleY(0.7); }
          .evt-dot-pulse { animation: none !important; }
          .evt-chip-nudge { animation: none !important; }
        }
      `}</style>
    </span>
  );
});

EvaVoiceTap.displayName = "EvaVoiceTap";

export default EvaVoiceTap;